import { computed, ref, shallowRef, watch } from 'vue';
import fastdomRaw from 'fastdom';
import { useEventListener, useMutationObserver, useResizeObserver } from "@vueuse/core";
import fastdomPromised from 'fastdom/extensions/fastdom-promised';
import { nextTick } from 'vue';

const fastdom = fastdomRaw.extend(fastdomPromised);

export const vSticky = {
    mounted: async (el, binding, vnode) => {
        const store = vnode.ctx.provides.store ?? {};

        const desktopOnly = binding.modifiers.desktop;
        const zIndex = binding.value?.zIndex;
        const parent = el.parentElement;

        const isMobile = computed(() => store.state.app.isMobile);
        const headerHeight = computed(() => store.state.app.headerHeight || store.state.app.headerFloatHeight + store.state.app.headerFloatTop);

        const elSize = shallowRef({});
        const parentSize = shallowRef({});

        useResizeObserver(el, ([entry]) => {
            if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                const { blockSize, inlineSize } = entry.borderBoxSize[0];
                elSize.value = {
                    height: blockSize,
                    width: inlineSize,
                }
            } else {
                fastdom.measure(() => {
                    const { width, height } = entry.target.getBoundingClientRect();
                    elSize.value = {
                        height,
                        width,
                    }
                });
            }
        });

        const placeholder = document.createElement('div');
        placeholder.style.width = '100%';
        placeholder.dataset.stickyPlaceholder = '';

        const bodyTop = ref(-window.scrollY);
        const bodyLeft = ref(-window.scrollX);
        const parentRect = parent.getBoundingClientRect();
        const parentPageTop = ref(parentRect.top - bodyTop.value);
        const parentPageLeft = ref(parentRect.left - bodyLeft.value);
        const placeholderPageTop = ref(0); // first measuring when it inserted in DOM (on event mutation observer)

        const measurePositions = () => {
            fastdom.measure(() => {
                const parentRect = parent.getBoundingClientRect();
                const placeholderRect = placeholder.getBoundingClientRect();

                bodyTop.value = -window.scrollY;
                bodyLeft.value = -window.scrollX;
                parentPageTop.value = parentRect.top - bodyTop.value;
                parentPageLeft.value = parentRect.left - bodyLeft.value;
                placeholderPageTop.value = placeholderRect.top - bodyTop.value;
            });
        }
        useEventListener(['scroll', 'animateBlocksComplete', 'animateBlocksProgress', 'resize'], () => {
            measurePositions();
        });

        useMutationObserver(document.body, () => {
            measurePositions();
        }, { childList: true, characterData: true, subtree: true });

        measurePositions();

        const parentOpacity = ref(window.getComputedStyle(parent).opacity);
        useEventListener(['animateBlocksComplete', 'animateBlocksProgress', 'animationend'], () => {
            fastdom.measure(() => {
                parentOpacity.value = window.getComputedStyle(parent).opacity;
            });
        });

        useResizeObserver(parent, ([entry]) => {
            if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                const { blockSize, inlineSize } = entry.borderBoxSize[0];
                parentSize.value = {
                    height: blockSize,
                    width: inlineSize,
                }
            } else {
                fastdom.measure(() => {
                    const { width, height } = entry.target.getBoundingClientRect();
                    parentSize.value = {
                        height,
                        width,
                    }
                });
            }
        });

        
        const wrapperHeight = computed(() => parentSize.value.height - (placeholderPageTop.value - parentPageTop.value))

        watch(() => elSize.value.height, (elHeight) => {
            fastdom.mutate(() => {
                placeholder.style.height = `${elHeight}px`;
            })
        }, { immediate: true });

        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        if (zIndex) wrapper.style.zIndex = zIndex;
        watch(() => parentSize.value.width, (val) => {
            fastdom.mutate(() => {
                wrapper.style.width = `${val}px`;
            })
        }, { immediate: true });

        watch(wrapperHeight, (val) => {
            fastdom.mutate(() => {
                wrapper.style.height = `${val}px`;
            })
        }, { immediate: true });

        watch(placeholderPageTop, (val) => {
            fastdom.mutate(() => {
                wrapper.style.top = `${val}px`;
            })
        }, { immediate: true });

        watch(parentPageLeft, (val) => {
            fastdom.mutate(() => {
                wrapper.style.left = `${val}px`;
            })
        }, { immediate: true });

        watch(parentOpacity, (val) => {
            fastdom.mutate(() => {
                wrapper.style.opacity = val;
            })
        }, { immediate: true });

        const appEl = document.getElementById('app');
        

        watch(isMobile, (isMobile) => {
            if (isMobile && desktopOnly) {
                fastdom.mutate(() => {
                    if (!parent.contains(placeholder)) return;

                    parent.replaceChild(el, placeholder);
                    appEl.removeChild(wrapper);
                    el.style.top = `0px`;
                    el.style.position = 'relative';
                });

                return;
            }


            fastdom.mutate(() => {
                parent.replaceChild(placeholder, el);
            })
                .then(() => nextTick())
                .then(() => fastdom.mutate(() => {
                    wrapper.style.top = `${placeholderPageTop.value}px`;
                    appEl.appendChild(wrapper);
                    wrapper.appendChild(el);
                    el.style.top = `${headerHeight.value}px`;
                    el.style.position = 'sticky';
                }));

            return;

        }, { immediate: true });

        watch(headerHeight, (val) => {
            if (isMobile.value && desktopOnly) return;

            fastdom.mutate(() => {
                el.style.top = `${val}px`;
            });
        });
    },
};

/**
 * Прикрепляет компонент к верху окна, со смещением `headerHeight`
 * в рамках контейнера (т.е. пока контейнер тоже скроллится в окне)
 *
 * поддерживает модификатор `v-sticky.desktop` - в таком случае будет работать только на десктопе
 * поддерживает значение `v-sticky="{ zIndex: number }"` - порядок относительно body
 *
 * TODO: вынести `headerHeight` в пропс или модификатор, убрать обработчики после размонтирования
 *
 */
export default {
    install(app) {
        app.directive('sticky', vSticky);
    }
}
